import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "react-i18next";
import AdvancedFilterModal from "./AdvancedFilterModal";
import CheckboxTreeNodeDto from "../../../types/dtos/generic/CheckboxTreeNodeDto";
import FilterCheckboxTreeApiResponseDto from "../../../types/analytics/FilterCheckboxTreeApiResponseDto";
import locationApi from "../../../api/dashboard/locationApi";
import jobTitleApi from "../../../api/dashboard/jobTitleApi";
import appraisalLevelApi from "../../../api/dashboard/appraisalLevelApi";
import userGroupApi from "../../../api/admin/userGroupApi";
import { BasicUserGroupDto } from "../../../types/admin/user-groups/BasicUserGroupDto";
import {
  AudienceFacetType,
  submitAudienceFacetPickerIds,
} from "../../../helpers/analytics/audienceFacetHelpers";

export type { AudienceFacetType };

interface AudienceFacetEditorProps {
  facetType: AudienceFacetType;
  selectedIds: number[] | null | undefined;
  /**
   * Required when commitOnChange is true. Optional when draft-until-Done
   * (commitOnChange false) — Done passes ids via onDone instead.
   */
  onSelectedIdsChange?(ids: number[]): void;
  /**
   * When false, checklist edits stay local until Done.
   * Default true preserves Advanced-panel drill behaviour that updates draft on each check.
   */
  commitOnChange?: boolean;
  /** When true, show a Done button that calls onDone. */
  showDoneButton?: boolean;
  /** Called after Done with the draft ids. */
  onDone?(ids: number[]): void;
}

/**
 * Audience facet checklist. Empty seed starts with nothing checked
 * so the user can pick options (unlike the legacy modal, which ticks everything
 * to mean “no restriction”).
 */
function AudienceFacetEditor({
  facetType,
  selectedIds,
  onSelectedIdsChange,
  commitOnChange = true,
  showDoneButton = false,
  onDone,
}: AudienceFacetEditorProps) {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth.user?.access_token;

  const seedIds = selectedIds ?? [];
  // Seed from staged/draft ids only when there is an existing restriction.
  // Empty seed → [] so the user picks rather than seeing everything ticked.
  const [checked, setChecked] = useState<string[]>(() =>
    seedIds.length === 0 ? [] : seedIds.map(String)
  );
  const [expanded, setExpanded] = useState<string[]>([]);

  const [locations, setLocations] = useState<CheckboxTreeNodeDto[]>();
  const [isLocationsLoading, setIsLocationsLoading] = useState(false);
  const [jobTitles, setJobTitles] = useState<CheckboxTreeNodeDto[]>();
  const [isJobTitlesLoading, setIsJobTitlesLoading] = useState(false);
  const [appraisalLevels, setAppraisalLevels] =
    useState<CheckboxTreeNodeDto[]>();
  const [isAppraisalLevelsLoading, setIsAppraisalLevelsLoading] =
    useState(false);
  const [userGroups, setUserGroups] = useState<BasicUserGroupDto[]>();
  const [isUserGroupsLoading, setIsUserGroupsLoading] = useState(false);

  useEffect(() => {
    if (facetType === "LOCATION" && locations === undefined) {
      setIsLocationsLoading(true);
      new locationApi(token).getLocationCheckboxTreeItemsByClientId(
        false,
        (data: FilterCheckboxTreeApiResponseDto) => {
          setLocations(data.nodes);
          setIsLocationsLoading(false);
        },
        (error) => {
          console.error(error);
          setIsLocationsLoading(false);
        }
      );
    }
    if (facetType === "JOB-TITLE" && jobTitles === undefined) {
      setIsJobTitlesLoading(true);
      new jobTitleApi(token).getJobTitleCheckboxTreeItemsByClientId(
        (data: FilterCheckboxTreeApiResponseDto) => {
          setJobTitles(data.nodes);
          setIsJobTitlesLoading(false);
        },
        (error) => {
          console.error(error);
          setIsJobTitlesLoading(false);
        }
      );
    }
    if (facetType === "APPRAISAL-LEVEL" && appraisalLevels === undefined) {
      setIsAppraisalLevelsLoading(true);
      new appraisalLevelApi(
        token
      ).getAppraisalLevelCheckboxTreeItemsByClientId(
        (data: FilterCheckboxTreeApiResponseDto) => {
          setAppraisalLevels(data.nodes);
          setIsAppraisalLevelsLoading(false);
        },
        (error) => {
          console.error(error);
          setIsAppraisalLevelsLoading(false);
        }
      );
    }
    if (facetType === "USER-GROUP" && userGroups === undefined) {
      setIsUserGroupsLoading(true);
      new userGroupApi(token).getAnalyticsUserGroups(
        (data) => {
          setUserGroups(data);
          setIsUserGroupsLoading(false);
        },
        (error) => {
          console.error(error);
          setIsUserGroupsLoading(false);
        }
      );
    }
  }, [facetType, token, locations, jobTitles, appraisalLevels, userGroups]);

  const applyChecked = (next: string[]) => {
    setChecked(next);
    if (commitOnChange) {
      onSelectedIdsChange?.(submitAudienceFacetPickerIds(next));
    }
  };

  const handleDone = () => {
    const ids = submitAudienceFacetPickerIds(checked);
    if (commitOnChange) {
      onSelectedIdsChange?.(ids);
    }
    onDone?.(ids);
  };

  return (
    <div>
      <AdvancedFilterModal
        filterType={facetType}
        onClearSelections={() => applyChecked([])}
        locations={locations}
        isLocationsLoading={isLocationsLoading}
        locationTreeChecked={checked}
        locationTreeExpanded={expanded}
        onLocationTreeChecked={applyChecked}
        onLocationTreeExpanded={setExpanded}
        appraisalLevels={appraisalLevels}
        isAppraisalLevelsLoading={isAppraisalLevelsLoading}
        appraisalLevelTreeChecked={checked}
        appraisalLevelTreeExpanded={expanded}
        onAppraisalLevelTreeChecked={applyChecked}
        onAppraisalLevelTreeExpanded={setExpanded}
        jobTitles={jobTitles}
        isJobTitlesLoading={isJobTitlesLoading}
        jobTitleTreeChecked={checked}
        jobTitleTreeExpanded={expanded}
        onJobTitleTreeChecked={applyChecked}
        onJobTitleTreeExpanded={setExpanded}
        userGroups={userGroups}
        isUserGroupsLoading={isUserGroupsLoading}
        userGroupTreeChecked={checked}
        userGroupTreeExpanded={expanded}
        onUserGroupTreeChecked={applyChecked}
        onUserGroupTreeExpanded={setExpanded}
        listMaxHeight={showDoneButton ? 200 : undefined}
      />
      {showDoneButton && (
        <div className="flex justify-end mt-3">
          <button
            type="button"
            className="btn-primary !px-3 !py-1.5 text-sm rounded-md"
            onClick={handleDone}
          >
            {t("Common.Done", "Done")}
          </button>
        </div>
      )}
    </div>
  );
}

export default AudienceFacetEditor;
